import {initializeApp} from 'firebase/app';
import {getAuth, onIdTokenChanged, signOut} from 'firebase/auth';
import 'firebase/auth';
import {
  clearCurrentUser,
  fetchDevices,
  fetchFollows,
  fetchFuPaUser,
  updateCurrentUser,
} from 'app/actions/currentUserActions';
import {setAuthorizationHeader} from 'app/services/api';
import {validateAuthStateLocalToken} from 'app/hooks/useAuthState';
import {setDimensionToGA4} from 'app/helpers/gtagHelpers';
import {removeElementFromLocalStorage, setElementToLocalStorage} from 'app/helpers/webStorageUtility';
import * as Sentry from '@sentry/react';

let auth;
let nativeApp;

const initFirebase = (dispatch, nativeInfo) => {
  const firebaseConfig = JSON.parse(process.env.FIREBASE_CONFIG);
  // Initialize Firebase
  initializeApp(firebaseConfig);
  auth = getAuth();
  onCurrentUserIdTokenChanged(dispatch);
  nativeApp = nativeInfo;
};

/* ++++ FIREBASE AUTHENTICATION ++++ */

// Used to sync to redux to show components depending on auth state
const onCurrentUserIdTokenChanged = dispatch => {
  onIdTokenChanged(auth, user => {
    if (user) {
      // Get IdToken decoded, with claims (including emailVerified) and token base64 encoded
      user.getIdTokenResult().then(decodedIdToken => {
        setDimensionToGA4({user_id: user.uid});
        Sentry.setUser({id: user.uid});
        if (nativeApp.isNativeApp) {
          nativeApp.setUserId.handler(user.uid);
        }
        // set authorization header for all api requests
        setElementToLocalStorage('pwa-token', decodedIdToken.token);
        setAuthorizationHeader(decodedIdToken.token);
        dispatch(updateCurrentUser({user, claims: decodedIdToken.claims}));
        if (decodedIdToken.claims?.u && decodedIdToken.claims?.email_verified) {
          dispatch(fetchFuPaUser());
          dispatch(fetchFollows());
          dispatch(fetchDevices());
        }
      });
    } else {
      removeElementFromLocalStorage('pwa-token');
      setAuthorizationHeader(null);
      Sentry.setUser(null);
      if (nativeApp.isNativeApp) {
        nativeApp.setUserId.handler(null);
      }
      dispatch(clearCurrentUser());
    }
  });
};

const checkExpirationOfToken = (user, expiryTime) => {
  // unix timestamps used for calculation
  const fifteenMinutes = 15 * 60;
  const currentTime = Math.round(new Date().getTime() / 1000);
  if (expiryTime - fifteenMinutes <= currentTime) {
    return forceTokenRefresh(user);
  } else {
    // Needs always to return a promise, otherwise errors are thrown
    return new Promise(resolve => {
      resolve();
    });
  }
};

const forceTokenRefresh = user => {
  return user.getIdToken(true);
};

const logoutCurrentUser = () => {
  return signOut(auth);
};

const tokenValidationForXHR = () => {
  const {hasValidLocalToken, token} = validateAuthStateLocalToken();
  if (auth?.currentUser) {
    return auth.currentUser.getIdToken();
  } else if (hasValidLocalToken) {
    return new Promise(resolve => resolve(token));
  } else {
    return new Promise((resolve, reject) => reject({status: 401, message: 'Unauthorized: no user set'}));
  }
};

export {
  initFirebase,
  onCurrentUserIdTokenChanged,
  checkExpirationOfToken,
  logoutCurrentUser,
  forceTokenRefresh,
  tokenValidationForXHR,
};
